// frontend/src/pages/AdminIOSCalendarLink.jsx
import React from "react";
import { isIOS, isAndroid } from "../utils/device";

// Base URL for the app (must be HTTPS for iOS subscriptions)
const DEFAULT_BASE =
  typeof window !== "undefined" ? window.location.origin : "";
const ICS_BASE =
  (process.env.REACT_APP_ICS_BASE || DEFAULT_BASE || "").replace(/\/+$/, "");

const isHttps = /^https:\/\//i.test(ICS_BASE);

// Our backend endpoint is /api/calendar/engineer.ics
const httpsFeed =
  isHttps && ICS_BASE
    ? `${ICS_BASE.replace(/^webcal:/i, "https:")}/api/calendar/engineer.ics`
    : "";
const webcalFeed = httpsFeed
  ? httpsFeed.replace(/^https:/i, "webcal:")
  : "";

export default function AdminIOSCalendarLink() {
  const ios = isIOS();
  const android = isAndroid();

  // Primary link to *use* on this device
  const primaryLink = ios ? webcalFeed : httpsFeed;

  const copy = async () => {
    if (!primaryLink) return;
    try {
      await navigator.clipboard.writeText(primaryLink);
      alert("Calendar link copied!");
    } catch {
      alert("Copy failed — long-press to copy the link below.");
    }
  };

  const handleSubscribeClick = () => {
    if (!primaryLink) return;
    window.location.assign(primaryLink);
  };

  const deviceLabel = ios
    ? "Subscribe on iPhone"
    : android
    ? "Open on Android"
    : "Open in calendar";

  return (
    <div className="container al-root">
      <style>{`
        .al-root { width: 100%; max-width: 100%; overflow-x: hidden; }
        .al-root, .al-root * { box-sizing: border-box; }
        .al-card { width: 100%; }
        .al-divider { border-top: 1px solid var(--border); margin: 8px 0 16px; }
        .al-actions { display: flex; gap: 8px; flex-wrap: wrap; }
        .al-note, .al-share { font-size: 14px; }
        .al-code { display:block; padding:8px; background:#f5f5f5; border-radius:6px; width:100%;
                   white-space:pre-wrap; overflow-wrap:anywhere; word-break:break-word; overflow:auto; }
        .al-warning { margin-top:12px; padding:10px; border-radius:8px; border:1px solid #fecaca;
                      background:#fee2e2; color:#991b1b; font-size:13px; }
      `}</style>

      <div className="card al-card" style={{ padding: 16 }}>
        <h2 className="section-title" style={{ margin: 0 }}>
          Engineer calendar link
        </h2>
        <div className="al-divider" />

        {!isHttps && (
          <div className="al-warning">
            Set <code>REACT_APP_ICS_BASE</code> to an HTTPS domain (e.g.{" "}
            <code>https://jwcs.live</code>) in{" "}
            <code>frontend/.env.production</code>, rebuild, and redeploy.
            iOS will only subscribe to HTTPS/webcal URLs.
          </div>
        )}
        {!httpsFeed && isHttps && (
          <div className="al-warning">
            Couldn&apos;t build the calendar URL. Check{" "}
            <code>REACT_APP_ICS_BASE</code> and that the app is served from{" "}
            <code>{ICS_BASE || "your HTTPS domain"}</code>.
          </div>
        )}

        <p className="al-note" style={{ marginTop: 12 }}>
          Use this page on your phone or desktop to add the shared engineer
          calendar:
        </p>
        <ul className="al-note" style={{ marginTop: 4, paddingLeft: 18 }}>
          <li>
            <b>iPhone / iPad:</b> opens a subscription prompt using{" "}
            <code>webcal://</code>.
          </li>
          <li>
            <b>Android:</b> opens the <code>.ics</code> feed via{" "}
            <code>https://</code> (Calendar apps can subscribe/import).
          </li>
          <li>
            <b>Desktop:</b> downloads/opens <code>jobs.ics</code> in your
            calendar app (Outlook, Apple Calendar, etc.).
          </li>
        </ul>

        <div className="al-actions" style={{ marginTop: 12 }}>
          <button
            className="btn"
            onClick={handleSubscribeClick}
            disabled={!primaryLink}
          >
            {deviceLabel}
          </button>
          <button
            className="btn"
            onClick={copy}
            disabled={!primaryLink}
          >
            Copy calendar link
          </button>
        </div>

        <div className="al-share" style={{ marginTop: 12 }}>
          <div style={{ opacity: 0.7, marginBottom: 6 }}>
            Link used on this device:
          </div>
          <code className="al-code">
            {primaryLink || "—"}
          </code>

          <div style={{ opacity: 0.7, marginTop: 10 }}>
            <div>iOS webcal subscription URL:</div>
            <code className="al-code" style={{ marginTop: 4 }}>
              {webcalFeed || "—"}
            </code>
          </div>

          <div style={{ opacity: 0.7, marginTop: 10 }}>
            <div>Direct HTTPS feed URL:</div>
            <code className="al-code" style={{ marginTop: 4 }}>
              {httpsFeed || "—"}
            </code>
          </div>

          <div style={{ opacity: 0.7, marginTop: 10 }}>
            For testing in Safari you can open the HTTPS version:{" "}
            {httpsFeed ? (
              <a
                href={httpsFeed}
                target="_blank"
                rel="noreferrer"
              >
                {httpsFeed}
              </a>
            ) : (
              <em>—</em>
            )}
            . It should download a file starting with{" "}
            <code>BEGIN:VCALENDAR</code>.
          </div>
        </div>

        <div style={{ marginTop: 16, opacity: 0.85 }}>
          <b>Note:</b> Anyone with this link can see the shared engineer job
          schedule (read-only). Keep it within your team.
        </div>
      </div>
    </div>
  );
}
